import { useTranslation } from 'react-i18next';
import { useProjects } from '../../../core/api';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { LoadingSpinner } from '../../../core/components/LoadingSpinner';
import { ProjectCard } from './ProjectCard';

interface FeaturedDestinationsProps {
    config?: any;
}

export const FeaturedDestinations = ({ config }: FeaturedDestinationsProps) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const { data: projects, isLoading } = useProjects({});

    const title = config?.title?.[i18n.language] || t('explore.destinations.title', 'Featured Destinations');
    const subtitle = config?.description?.[i18n.language] || t('explore.destinations.subtitle', 'Explore top-tier developments.');

    const activeProjects = Array.isArray(projects) ? projects.slice(0, 5) : [];

    if (isLoading) return <LoadingSpinner />;
    if (!activeProjects.length) return null;

    return (
        <section className="py-8 bg-white border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
                {/* Section Header */}
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold text-secondary font-display">{title}</h2>
                        {subtitle && <p className="text-slate-500 text-sm mt-1 max-w-lg">{subtitle}</p>}
                    </div>
                    {/* View All Link (Optional) */}
                    <Link to="/projects" className="text-sm font-semibold text-primary hover:underline flex items-center gap-1">
                        {t('common.viewAll', 'View All')}
                        <ArrowRight className={isRTL ? "rotate-180 w-4 h-4" : "w-4 h-4"} />
                    </Link>
                </div>

                {/* Horizontal Scroll Container */}
                <div className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide -mx-4 px-4 sm:mx-0 sm:px-0 pointer-events-auto" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {activeProjects.map((project: any) => {
                        const cardData = {
                            id: project.id,
                            title: project.title || project.slug,
                            location: project.location?.name || project.area?.name || project.address || project.locationText || '',
                            specs: {
                                beds: project._count?.units || 0,
                                baths: 0,
                                area: project.areaRange || '—'
                            },
                            price: project.price,
                            payment: project.paymentPlan || t('common.installments', 'Installments'),
                            status: project.delivery || project.status || t('status.available', 'Available'),
                            image: project.image,
                            finishType: project.finish,
                            deliveryDate: project.delivery,
                            areaRange: project.areaRange,
                            propertyTypes: (project as any).categories?.map((c: any) => c.category?.translations?.[0]?.name || c.category?.slug).filter(Boolean) || []
                        };

                        return (
                            <ProjectCard
                                key={project.id}
                                project={cardData}
                                linkTo={`/projects/${project.slug || project.id}`}
                                className="min-w-[280px] sm:min-w-[340px] snap-center"
                                type="Project"
                                layout="project"
                            />
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
